/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2018, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
export default class ColorConversion {

	static convertRgbHexToRgbVal(rgbHex){
		let rgbColor = {};
		if( rgbHex ){
			rgbColor.red = parseInt( rgbHex.substr(1, 2), 16 ) || 0;
			rgbColor.green = parseInt( rgbHex.substr(3, 2), 16 ) || 0;
			rgbColor.blue = parseInt( rgbHex.substr(5, 2), 16 ) || 0;
		}
		return rgbColor;
	}

	static convertRgbValToRgbHex(rgbVal){
		let redCode = Math.round( rgbVal.red ).toString( 16 );
		let greenCode = Math.round( rgbVal.green ).toString( 16 );
		let blueCode = Math.round( rgbVal.blue ).toString( 16 );

		while (redCode.length < 2) {
			redCode = '0' + redCode;
		}
		while (greenCode.length < 2) {
			greenCode = '0' + greenCode;
		}
		while (blueCode.length < 2) {
			blueCode = '0' + blueCode;
		}
		return ('#' + redCode + greenCode + blueCode).toUpperCase();
	}

	static convertRgbToHsl(color){
		if(!color){
			return {};
		}
		let rgbVal = this.convertRgbHexToRgbVal(color);
		let hslVal = {};

		let redVal = rgbVal.red/255;
		let greenVal = rgbVal.green/255;
		let blueVal = rgbVal.blue/255;

		let maxVal = Math.max(redVal, greenVal, blueVal);
		let minVal = Math.min(redVal, greenVal, blueVal);

		let chroma = maxVal - minVal;

		let lightnessVal = (maxVal + minVal)/2;

		let hueVal = 0;

		if(chroma == 0){
			hueVal = 0;
		}else{
			if(maxVal == redVal){
				hueVal = ((greenVal - blueVal) / chroma) % 6;
			}else if(maxVal == greenVal){
				hueVal = ((blueVal - redVal) / chroma) + 2;
			}else if(maxVal == blueVal){
				hueVal = ((redVal - greenVal) / chroma) + 4;
			}
		}

		let satVal = chroma/(1 - Math.abs(2 * lightnessVal - 1));
		if(!satVal){
			satVal = 0;
		}
		hslVal.hue = hueVal * 60;

		if(hslVal.hue < 0){
			hslVal.hue += 360;
		}else if(hslVal.hue > 360){
			hslVal.hue -= 360;
		}

		hslVal.sat = satVal;
		hslVal.light = lightnessVal;

		return hslVal;
	}

	static convertHslToRgb(hslColor){
		let chroma = (1 - Math.abs((2 * hslColor.light) - 1)) * hslColor.sat;
		let huePrime = hslColor.hue/60;
		let rgbColor = {};
		let variance = 1 * (hslColor.light - (chroma/2));

		let colorShift = chroma * (1 -  Math.abs((huePrime % 2) - 1));

		if(huePrime >= 0 && huePrime <= 1){
			rgbColor.red = chroma;
			rgbColor.green = colorShift;
			rgbColor.blue = 0;
		}else if(huePrime >= 1 && huePrime <= 2){
			rgbColor.red = colorShift;
			rgbColor.green = chroma;
			rgbColor.blue = 0;
		}else if(huePrime >= 2 && huePrime <= 3){
			rgbColor.red = 0;
			rgbColor.green = chroma;
			rgbColor.blue = colorShift;
		}else if(huePrime >= 3 && huePrime <= 4){
			rgbColor.red = 0;
			rgbColor.green = colorShift;
			rgbColor.blue = chroma;
		}else if(huePrime >= 4 && huePrime <= 5){
			rgbColor.red = colorShift;
			rgbColor.green = 0;
			rgbColor.blue = chroma;
		}else if(huePrime >= 5 && huePrime <= 6){
			rgbColor.red = chroma;
			rgbColor.green = 0;
			rgbColor.blue = colorShift;
		}else{
			rgbColor.red = 0;
			rgbColor.green = 0;
			rgbColor.blue = 0;
		}

		rgbColor.red = (rgbColor.red + variance) * 255;
		rgbColor.green = (rgbColor.green + variance) * 255;
		rgbColor.blue = (rgbColor.blue + variance) * 255;

		return rgbColor;
	}

	/**
	 * Hex code with opacity: RRGGBBAA where AA is the alpha opacity value
	 *
	 * @param {string} hexColor - hex color code, e.g., #FFAABB
	 * @param {number} opacity - fraction number represent the opacity. The value must be between 0 and 1
	 *
	 * @return {string} An 8 digit value represent the hex color with opacity
	 */
	static convertHexColorToHexColorWithOpacity (hexColor, opacity) {
		if (typeof hexColor !== 'string') {
			throw new Error ('Invalid hexColor');
		}
		if (typeof opacity !== 'number') {
			throw new Error ('Invalid opacity value');
		}
		if (opacity < 0 || opacity > 1) {
			throw new Error ('The opacity value must be between 0.0 and 1.0');
		}
		const alpha = Math.round(opacity * 255);
		let hexAlpha = alpha.toString(16).toUpperCase();
		if (hexAlpha.length === 1) {
			hexAlpha = `0${hexAlpha}`;
		}
		return `${hexColor.toUpperCase()}${hexAlpha}`;
	}
}



// WEBPACK FOOTER //
// ./src/js/utilities/ColorConversion.js